import type {
  FootAttached,
  FootDetached,
  FootDetachReason,
  FootId,
  GrindExit,
  RiderBailed,
} from "../../../shared";
import { FOOT_IDS, Quat, Transform, Vec3 } from "../../../shared";
import type { RiderConfig } from "../rider.config";
import {
  axisErrorRad,
  boardForward,
  boardHeadingRad,
  boardUp,
  deckPointWorld,
  offAngleTouchUpDot,
  spotStanding,
  spotUnder,
  supportNormal,
  wrapPi,
} from "./board-geometry";
import { DeckPosition } from "./deck-position";
import { clampToDeck, deckTopPointLocal, isOnDeck } from "./deck-surface";
import { Foot, type FootFrame, type FootMotionLimits } from "./foot";
import type { BoardKinematics, DeckGeometry, RiderControls } from "./foot-force-model";
import { feetPressure, targetDeckPosition } from "./foot-placement";
import type { RiderGrind, RiderState } from "./rider-state";

export { boardUp, tiltRad } from "./board-geometry";

type WithoutMeta<E> = Omit<E, "tick" | "timeS">;

/**
 * What changed in the aggregate during one update: the payload of a domain event minus
 * `tick`/`timeS`, which the application adds from the snapshot it is processing.
 */
export type RiderChange =
  | WithoutMeta<FootAttached>
  | WithoutMeta<FootDetached>
  | WithoutMeta<RiderBailed>;

/** Neutral controls (both sticks centred, feet-down key up). */
export const NEUTRAL_CONTROLS: RiderControls = Object.freeze({
  front: Object.freeze({ foot: "front", stick: { x: 0, y: 0 }, stickVelocityPerS: { x: 0, y: 0 } }),
  back: Object.freeze({ foot: "back", stick: { x: 0, y: 0 }, stickVelocityPerS: { x: 0, y: 0 } }),
  feetDown: false,
  stance: "regular",
});

/**
 * `Rider` aggregate (REQUIREMENTS §1.3, MECHANICS.md): both feet, the torso, the rider
 * frame and the bail state.
 *
 * - The RIDER FRAME is the torso position plus a yaw-only heading, always upright. The
 *   heading follows the board's long axis (whichever way round is closer) while grounded,
 *   rate limited, and is held in the air, so a shove-it spins the board under still feet.
 * - Feet are KINEMATIC points in the rider frame at their stick targets (an attached foot
 *   slides there at most `maxSlideSpeedMps`; on the ground it stays over the deck). They
 *   never rotate with the board. An attached foot is drawn on the grip tape under it; an
 *   airborne one hovers above the level deck under the torso.
 * - The pop lifts both feet off (`liftFeet`); a catch snaps them back on (`catchFeet`).
 *   An uncaught board that lands roughly level and on its wheels gets its feet back too.
 * - Bail: an uncaught landing that is not level, a caught landing that is badly tilted,
 *   the board upside down (`land`), a deck / tail / nose touchdown outside the landing
 *   tolerance, both feet off on the wheels, or the board resting upside down. The bail is
 *   RAGDOLL (MECHANICS.md "Bail: the board goes ragdoll"): the feet let go at once and fall
 *   away with the torso, and every key is ignored until the reset.
 */
export class Rider {
  private readonly feet: Record<FootId, Foot>;
  private torsoPositionM: Vec3 = Vec3.ZERO;
  private torsoVelocityMps: Vec3 = Vec3.ZERO;
  /** A foot is on the deck: the torso rides the board (plus `torsoOffsetM`, easing out). */
  private torsoStanding = true;
  private torsoOffsetM: Vec3 = Vec3.ZERO;
  private headingRad = 0;
  private windUpRad = 0;
  private bodySpinRateRadps = 0;
  /** A pop out of a slide turns the body back this much more (rad, signed). */
  private realignLeftRad = 0;
  private popOutTurnRad = 0;
  /** SPIN SNAP (assists): the heading a released body spin eases to a stop at, or null. */
  private snapHeadingRad: number | null = null;
  private grind: RiderGrind | null = null;
  private kickflipFlick: FootId | null = null;
  private lastGrindExit: GrindExit | null = null;
  private bailed = false;
  private bothFeetOffOnWheelsS = 0;
  private upsideDownRestingS = 0;
  /** How long a deck / tail / nose has touched down outside the tolerance, s. */
  private offAngleTouchS = 0;
  private cachedState: RiderState | null = null;
  private readonly footLimits: FootMotionLimits;

  constructor(
    private readonly deck: DeckGeometry,
    private readonly config: RiderConfig,
    board: BoardKinematics,
  ) {
    const { maxFootSpeedMps, maxFootAccelMps2, catchReachS } = config.feet;
    // Ease-out time constant: most of a catch gap closes within `catchReachS`.
    this.footLimits = {
      maxSpeedMps: maxFootSpeedMps,
      maxAccelMps2: maxFootAccelMps2,
      easeS: catchReachS / 3,
    };
    this.feet = {
      front: new Foot("front", DeckPosition.create(0, 0)),
      back: new Foot("back", DeckPosition.create(0, 0)),
    };
    this.reset(board);
  }

  get state(): RiderState {
    this.cachedState ??= Object.freeze({
      front: this.feet.front.toState(),
      back: this.feet.back.toState(),
      torsoPositionWorldM: this.torsoPositionM,
      headingRad: this.headingRad,
      windUpRad: this.windUpRad,
      bodySpinRateRadps: this.bodySpinRateRadps,
      bailed: this.bailed,
      grind: this.grind,
      lastGrindExit: this.lastGrindExit,
      popOutTurnRad: this.popOutTurnRad,
      kickflipFlick: this.kickflipFlick,
    });
    return this.cachedState;
  }

  /**
   * The pop: the rider jumps and both feet leave the deck. The stored wind-up becomes the
   * initial body spin (`windUpSpinRadps` × the wind-up fraction).
   */
  liftFeet(realignRad = 0): readonly RiderChange[] {
    const { windUpMaxRad, windUpSpinRadps } = this.config.tricks;
    this.bodySpinRateRadps = (this.windUpRad / windUpMaxRad) * windUpSpinRadps;
    this.windUpRad = 0;
    this.realignLeftRad = realignRad;
    this.popOutTurnRad = realignRad;
    this.cachedState = null;
    return this.detachAll("jumped");
  }

  /** The catch: both feet snap back onto the deck. */
  catchFeet(board: BoardKinematics): readonly RiderChange[] {
    if (this.bailed) return [];
    const changes: RiderChange[] = [];
    for (const id of FOOT_IDS) {
      const foot = this.feet[id];
      if (foot.isAttached) continue;
      foot.attach();
      // The spot is taken now; the drawn foot eases onto it over `catchReachS`.
      this.placeFoot(foot, board, 0);
      const at = foot.deckPosition;
      changes.push({
        type: "FootAttached",
        foot: id,
        deckPosition: { alongM: at.alongM, acrossM: at.acrossM },
      });
    }
    this.cachedState = null;
    return changes;
  }

  /**
   * The grind lock this step (MECHANICS.md M4), from the trick model: the read model shows
   * it, the heading lines up with the board while locked, and losing the balance
   * (`fellOff`) is a bail.
   */
  setGrind(grind: RiderGrind | null, exit: GrindExit | null): readonly RiderChange[] {
    if (grind !== null) {
      this.grind = {
        kind: grind.kind,
        side: grind.side,
        obstacleId: grind.obstacleId,
        surface: grind.surface,
        balance: grind.balance,
      };
      this.lastGrindExit = null;
    } else {
      this.grind = null;
    }
    if (exit !== null) this.lastGrindExit = exit;
    this.cachedState = null;
    if (exit === "fellOff" && !this.bailed) return this.bail("lostBalance");
    return [];
  }

  /**
   * SPIN SNAP (MECHANICS.md "Assists" 2): the trick model's stance angle to a grind edge
   * ahead for a released body spin to stop at, or null. Applied in the air only.
   */
  snapSpinTo(headingRad: number | null): void {
    this.snapHeadingRad = headingRad;
  }

  /** The foot flicking a kickflip this step (visual: its toes point down), or null. */
  setKickflipFlick(foot: FootId | null): void {
    const next = this.bailed ? null : foot;
    if (next === this.kickflipFlick) return;
    this.kickflipFlick = next;
    this.cachedState = null;
  }

  /** Loop step 5: moves the torso and feet and checks for a bail. */
  update(
    input: RiderControls,
    board: BoardKinematics,
    dtS: number,
    loading = false,
  ): readonly RiderChange[] {
    this.cachedState = null;
    // Bailed: every key is ignored until the reset (the feet stay where they let go).
    const controls = this.bailed ? NEUTRAL_CONTROLS : input;
    this.updateWindUp(controls, board, loading && !this.bailed, dtS);
    if (!this.bailed) this.updateHeading(controls, board, dtS);
    this.updateTorso(board, dtS);
    for (const id of FOOT_IDS) {
      const foot = this.feet[id];
      foot.tick(dtS);
      this.moveHold(foot, targetDeckPosition(id, controls[id].stick, this.config.feet), board, dtS);
      this.placeFoot(foot, board, dtS);
    }
    const pressure = feetPressure(
      controls,
      { front: this.feet.front.deckPosition, back: this.feet.back.deckPosition },
      this.deck,
      this.config,
    );
    for (const id of FOOT_IDS) this.feet[id].setPressure(pressure[id]);
    const changes: RiderChange[] = [];
    if (!this.bailed) this.checkBail(board, dtS, changes);
    return changes;
  }

  /**
   * Called when the board lands, with its up vector dotted with the landing SURFACE's
   * normal (`BoardLanded.surfaceUpDot`, so banks and transitions count as level; world up
   * when unknown). Upside down or sideways to the
   * travel → bail. Tilted beyond `landTiltRad` → bail, caught or not (the catch is feet,
   * not magic). Uncaught and roughly level: the feet come back.
   */
  land(upDot: number, board: BoardKinematics): readonly RiderChange[] {
    if (this.bailed) return [];
    if (upDot < 0) return this.bail("upsideDown");
    // Too sideways to roll (neither forward nor fakie along the travel): bail, never a
    // violent redirect by the wheel grip.
    if (this.landsSideways(board)) return this.bail("offAngle");
    const tilt = Math.acos(Math.max(-1, Math.min(1, upDot)));
    // Caught or not, the same tolerance: being caught never skips the landing check.
    if (tilt > this.config.tricks.landTiltRad) return this.bail("offAngle");
    const caught = this.feet.front.isAttached || this.feet.back.isAttached;
    return caught ? [] : this.catchFeet(board);
  }

  /**
   * MECHANICS.md "Landing": the board must line up with the direction of travel, forward or
   * fakie, within `landYawToleranceRad`. Measured between the board's long axis and its
   * velocity in the plane of the landing surface, so it also holds on banks and
   * transitions (where headings in the horizontal plane mean little). Skipped when slow (no clear travel) and over a grindable
   * obstacle (the M4 boardslide hook).
   */
  private landsSideways(board: BoardKinematics): boolean {
    if (board.contactPoints?.some((c) => c.surface === "grindable") === true) return false;
    // In the plane of the landing surface (the fall into it does not count).
    const n = supportNormal(board);
    const inPlane = (u: Vec3): Vec3 => Vec3.sub(u, Vec3.scale(n, Vec3.dot(u, n)));
    const v = inPlane(board.linearVelocityMps);
    const f = inPlane(boardForward(board));
    const speed = Vec3.length(v);
    if (speed < this.config.torso.headingTravelMinSpeedMps || Vec3.length(f) < 1e-6) return false;
    const along = Math.abs(Vec3.dot(f, v)) / (speed * Vec3.length(f));
    const off = Math.acos(Math.min(1, along));
    return off > this.config.tricks.landYawToleranceRad;
  }

  /** Both feet back on the deck at their rest positions, torso above the board, not bailed. */
  reset(board: BoardKinematics): void {
    this.cachedState = null;
    this.bailed = false;
    this.bothFeetOffOnWheelsS = 0;
    this.upsideDownRestingS = 0;
    this.offAngleTouchS = 0;
    this.headingRad = boardHeadingRad(board) ?? 0;
    this.windUpRad = 0;
    this.bodySpinRateRadps = 0;
    this.realignLeftRad = 0;
    this.popOutTurnRad = 0;
    this.snapHeadingRad = null;
    this.kickflipFlick = null;
    this.grind = null;
    this.lastGrindExit = null;
    this.torsoPositionM = this.torsoTarget(board);
    this.torsoVelocityMps = board.linearVelocityMps;
    this.torsoStanding = true;
    this.torsoOffsetM = Vec3.ZERO;
    for (const id of FOOT_IDS) {
      const foot = this.feet[id];
      foot.attach();
      foot.holdAt(targetDeckPosition(id, { x: 0, y: 0 }, this.config.feet));
      foot.setPressure(0);
      this.placeFoot(foot, board, null);
    }
  }

  // ── feet ──────────────────────────────────────────────────────────────────

  private detachAll(reason: FootDetachReason): readonly RiderChange[] {
    if (this.bailed) return [];
    const changes: RiderChange[] = [];
    for (const id of FOOT_IDS) {
      const foot = this.feet[id];
      if (!foot.isAttached) continue;
      foot.detach();
      changes.push({ type: "FootDetached", foot: id, reason });
    }
    this.cachedState = null;
    return changes;
  }

  /**
   * An attached foot slides toward its target in the rider frame (at most
   * `maxSlideSpeedMps`); while grounded it stays over the deck (leaning does not step
   * off). An airborne foot is simply held at its target.
   */
  private moveHold(foot: Foot, target: DeckPosition, board: BoardKinematics, dtS: number): void {
    if (!foot.isAttached) {
      foot.holdAt(target);
      return;
    }
    const wanted = board.grounded ? clampToDeck(this.deck, target) : target;
    foot.holdAt(moveToward(foot.riderPosition, wanted, this.config.feet.maxSlideSpeedMps * dtS));
  }

  /**
   * The goal: attached, on the grip tape under the rider-frame position (clamped to the
   * deck); airborne, hovering `airLiftM` above that position on a level deck under the
   * torso. An attached foot is drawn IN THE BOARD FRAME: it moves rigidly with the deck
   * whatever the board does, and only its motion relative to the deck (sliding on the grip,
   * a catch reaching it) is eased and limited. An airborne one moves in the rider frame with
   * the same limits. Switching frames keeps the world position (`dtS` = 0: goal only; null:
   * snap, for a reset), so attaching, detaching and sliding never jump.
   */
  private placeFoot(foot: Foot, board: BoardKinematics, dtS: number | null): void {
    const yaw = Quat.fromAxisAngle(Vec3.UNIT_Y, this.headingRad);
    const base = this.riderBase();
    const onDeckNow = foot.isAttached && boardUp(board).y > 0;
    // Standing on it, the feet keep their places on the deck however it is tilted; held
    // in the air, the spot is the one under the foot.
    const spot =
      (onDeckNow ? spotStanding(board, this.headingRad, foot.riderPosition) : null) ??
      spotUnder(board, this.heldWorld(foot.riderPosition, 0));
    const frame = onDeckNow ? "board" : "rider";
    let goalM: Vec3;
    if (onDeckNow) {
      const onDeck = isOnDeck(this.deck, spot) ? spot : clampToDeck(this.deck, spot);
      foot.setSpot(onDeck);
      goalM = Transform.toLocalPoint(board.transform, deckPointWorld(this.deck, board, onDeck));
    } else {
      foot.setSpot(spot);
      const heldAirM = this.heldWorld(foot.riderPosition, this.config.feet.airLiftM);
      goalM = Quat.inverseRotate(yaw, Vec3.sub(heldAirM, base));
    }
    const toWorld = (f: FootFrame, p: Vec3): Vec3 =>
      f === "board"
        ? Transform.toWorldPoint(board.transform, p)
        : Vec3.add(base, Quat.rotate(yaw, p));
    const toFrame = (f: FootFrame, worldM: Vec3): Vec3 =>
      f === "board"
        ? Transform.toLocalPoint(board.transform, worldM)
        : Quat.inverseRotate(yaw, Vec3.sub(worldM, base));
    if (dtS === null) foot.snapDrawn(frame, goalM);
    else if (dtS > 0) {
      if (foot.drawnFrame !== frame) {
        // Attaching or lifting off: carried by the rider frame through this step (the rider
        // moves the foot, not the board), then eased in the new frame.
        foot.rebase(frame, toFrame(frame, toWorld("rider", foot.riderPositionM)));
      }
      foot.followDrawn(goalM, dtS, this.footLimits);
    }
    // (With `dtS` = 0 the frame switch waits for the next step: the goal only.)
    const worldM = toWorld(foot.drawnFrame, foot.drawnPositionM);
    foot.setWorld(worldM, Quat.inverseRotate(yaw, Vec3.sub(worldM, base)));
  }

  /** The rider frame's origin: under the torso at deck height, m (world). */
  private riderBase(): Vec3 {
    return Vec3.sub(this.torsoPositionM, Vec3.create(0, this.config.torso.heightM, 0));
  }

  // ── bail ──────────────────────────────────────────────────────────────────

  private checkBail(board: BoardKinematics, dtS: number, changes: RiderChange[]): void {
    const touchdown = this.badTouchdown(board, dtS);
    if (touchdown !== null) {
      changes.push(...this.bail(touchdown));
      return;
    }
    const bothOff = !this.feet.front.isAttached && !this.feet.back.isAttached;
    this.bothFeetOffOnWheelsS = board.grounded && bothOff ? this.bothFeetOffOnWheelsS + dtS : 0;
    const touching = board.contacts.deck || board.contacts.tail || board.contacts.nose;
    const upsideDown = !board.grounded && touching && boardUp(board).y < 0;
    this.upsideDownRestingS = upsideDown ? this.upsideDownRestingS + dtS : 0;

    const { bail } = this.config;
    if (this.bothFeetOffOnWheelsS > bail.feetDetachedAfterLandingS) {
      changes.push(...this.bail("feetDetached"));
    } else if (this.upsideDownRestingS > bail.upsideDownRestS) {
      changes.push(...this.bail("upsideDown"));
    }
  }

  /**
   * BAIL AT TOUCHDOWN (MECHANICS.md "Bail: the board goes ragdoll"): a deck / tail / nose
   * touching down outside the landing tolerance (`offAngleTouchUpDot`) for at least
   * `touchdownHoldS` cannot be landed any more (a one-step graze of a kick mid-flip still
   * can): a bail, so no controller fights it. Upside down → `upsideDown`, else `offAngle`.
   * Not on an edge (the lock owns the board there); a wheel touchdown is `land`'s.
   */
  private badTouchdown(board: BoardKinematics, dtS: number): RiderBailed["reason"] | null {
    const { bail, tricks } = this.config;
    const upDot =
      this.grind === null
        ? offAngleTouchUpDot(board, bail.touchdownMinAirS, tricks.landTiltRad)
        : null;
    this.offAngleTouchS = upDot === null ? 0 : this.offAngleTouchS + dtS;
    if (upDot === null || this.offAngleTouchS < bail.touchdownHoldS - 1e-9) return null;
    return upDot < 0 ? "upsideDown" : "offAngle";
  }

  /**
   * The bail: the rider lets go of the board completely. Both feet detach (they fall away
   * with the torso, drawn semi-transparent) and from now on nothing the rider does reaches
   * the board until the reset.
   */
  private bail(reason: RiderBailed["reason"]): RiderChange[] {
    const changes: RiderChange[] = [{ type: "RiderBailed", reason }];
    for (const id of FOOT_IDS) {
      const foot = this.feet[id];
      if (!foot.isAttached) continue;
      foot.detach();
      changes.push({ type: "FootDetached", foot: id, reason: "bailed" });
    }
    this.bailed = true;
    this.kickflipFlick = null;
    this.bodySpinRateRadps = 0;
    this.realignLeftRad = 0;
    this.snapHeadingRad = null;
    this.windUpRad = 0;
    this.cachedState = null;
    return changes;
  }

  // ── torso / rider frame ───────────────────────────────────────────────────

  /** Above the board; bailed, fallen down near it (the rider hits the ground too). */
  private torsoTarget(board: BoardKinematics): Vec3 {
    const heightM = this.bailed ? this.config.bail.fallenTorsoHeightM : this.config.torso.heightM;
    return Vec3.add(board.transform.positionM, Vec3.create(0, heightM, 0));
  }

  /**
   * While a foot stands on the deck the rider frame's translation IS the board's (above
   * it): a push or a hard carve never leaves the rider behind (only the heading is
   * smoothed). With both feet off (the air, a bail) it is a spring-damper toward "above
   * the board", with velocity feed-forward, so a pop or a landing makes it lag a little.
   */
  private updateTorso(board: BoardKinematics, dtS: number): void {
    const standing = !this.bailed && (this.feet.front.isAttached || this.feet.back.isAttached);
    if (standing) {
      // Back on the deck (a catch, a landing): where the torso lagged is eased out over
      // `attachBlendS`, never snapped; the board's own motion is never lagged.
      if (!this.torsoStanding) {
        this.torsoOffsetM = Vec3.sub(this.torsoPositionM, this.torsoTarget(board));
      }
      const k = Math.exp(-dtS / Math.max(1e-3, this.config.torso.attachBlendS));
      this.torsoOffsetM =
        Vec3.lengthSq(this.torsoOffsetM) < 1e-12 ? Vec3.ZERO : Vec3.scale(this.torsoOffsetM, k);
      this.torsoStanding = true;
      this.torsoPositionM = Vec3.add(this.torsoTarget(board), this.torsoOffsetM);
      this.torsoVelocityMps = board.linearVelocityMps;
      return;
    }
    this.torsoStanding = false;
    this.torsoOffsetM = Vec3.ZERO;
    const { followOmegaRadps: w, followDampingRatio: zeta } = this.config.torso;
    const toTarget = Vec3.sub(this.torsoTarget(board), this.torsoPositionM);
    const velocityError = Vec3.sub(board.linearVelocityMps, this.torsoVelocityMps);
    const accel = Vec3.add(Vec3.scale(toTarget, w * w), Vec3.scale(velocityError, 2 * zeta * w));
    this.torsoVelocityMps = Vec3.add(this.torsoVelocityMps, Vec3.scale(accel, dtS));
    this.torsoPositionM = Vec3.add(this.torsoPositionM, Vec3.scale(this.torsoVelocityMps, dtS));
  }

  /**
   * The rider heading (yaw only) is never snapped to the board's yaw:
   * - in the air it is held (frozen at takeoff), so shove-its spin under still feet;
   * - on the ground it follows the direction of travel, either way round (riding fakie
   *   does not turn the rider), smoothed and rate limited — so carving turns the rider;
   * - only when slow does it follow the board's long axis (again either way round).
   */
  private updateHeading(controls: RiderControls, board: BoardKinematics, dtS: number): void {
    if (this.grind !== null) {
      // Locked on an edge: the body lines up with the board (either way round).
      this.bodySpinRateRadps = 0;
      const heading = boardHeadingRad(board);
      if (heading === null) return;
      const { headingMaxRateRadps, headingFollowPerS } = this.config.torso;
      const error = axisErrorRad(heading - this.headingRad);
      const rate = Math.max(
        -2 * headingMaxRateRadps,
        Math.min(2 * headingMaxRateRadps, error * headingFollowPerS),
      );
      this.headingRad = wrapPi(this.headingRad + rate * dtS);
      return;
    }
    if (!board.grounded) {
      this.spinBody(controls, dtS);
      return;
    }
    this.bodySpinRateRadps = 0;
    this.realignLeftRad = 0;
    const { headingFollowPerS, headingMaxRateRadps, headingTravelMinSpeedMps } = this.config.torso;
    const v = board.linearVelocityMps;
    const target =
      Math.hypot(v.x, v.z) >= headingTravelMinSpeedMps
        ? Math.atan2(-v.z, v.x)
        : boardHeadingRad(board);
    if (target === null) return;
    const error = axisErrorRad(target - this.headingRad);
    const rate = Math.max(
      -headingMaxRateRadps,
      Math.min(headingMaxRateRadps, error * headingFollowPerS),
    );
    this.headingRad = wrapPi(this.headingRad + rate * dtS);
  }

  /**
   * BODY SPIN in the air (MECHANICS.md "Body spin"): Q / E ease the heading's spin rate
   * toward ±`bodySpinRateRadps` (at most `bodySpinAccelRadps2`); released, it eases back to
   * 0, so the rider can stop at any angle. The only way the heading changes in the air.
   */
  private spinBody(controls: RiderControls, dtS: number): void {
    const { bodySpinRateRadps } = this.config.tricks;
    let accel = this.config.tricks.bodySpinAccelRadps2;
    // Q (spin −1) turns left: counter-clockwise seen from above = +heading.
    let wanted = -(controls.spin ?? 0) * bodySpinRateRadps;
    const snap = this.snapHeadingRad;
    if (snap !== null && this.realignLeftRad === 0) {
      // Released near a stance angle to the edge ahead: ease to a stop exactly on it.
      accel *= this.config.assist.spinSnapAccelScale;
      const error = wrapPi(snap - this.headingRad);
      wanted =
        Math.sign(error) * Math.min(bodySpinRateRadps, Math.sqrt(2 * accel * Math.abs(error)));
    }
    // Out of a slide: turn back toward the travel, easing to a stop on the quarter turn.
    const left = this.realignLeftRad;
    if (left !== 0) {
      wanted +=
        Math.sign(left) * Math.min(bodySpinRateRadps, Math.sqrt(2 * accel * Math.abs(left)));
    }
    const step = accel * dtS;
    const change = Math.max(-step, Math.min(step, wanted - this.bodySpinRateRadps));
    this.bodySpinRateRadps += change;
    const turn = this.bodySpinRateRadps * dtS;
    this.headingRad = wrapPi(this.headingRad + turn);
    if (left !== 0) {
      const rest = left - turn;
      this.realignLeftRad = Math.sign(rest) === Math.sign(left) ? rest : 0;
    }
  }

  /**
   * WIND-UP: while a pop is loaded on the ground, Q / E turn the shoulders toward
   * ±`windUpMaxRad` at `windUpRateRadps`; otherwise they unwind.
   */
  private updateWindUp(
    controls: RiderControls,
    board: BoardKinematics,
    loading: boolean,
    dtS: number,
  ): void {
    const { windUpMaxRad, windUpRateRadps } = this.config.tricks;
    const wanted = board.grounded && loading ? -(controls.spin ?? 0) * windUpMaxRad : 0;
    const step = windUpRateRadps * dtS;
    this.windUpRad += Math.max(-step, Math.min(step, wanted - this.windUpRad));
  }

  /**
   * Where the rider holds a foot: at its rider-frame position on an imaginary level deck
   * under the torso (following the kick heights), lifted by `liftM`.
   */
  private heldWorld(riderPosition: DeckPosition, liftM: number): Vec3 {
    const local = deckTopPointLocal(this.deck, riderPosition.alongM, riderPosition.acrossM);
    const offset = Quat.rotate(
      Quat.fromAxisAngle(Vec3.UNIT_Y, this.headingRad),
      Vec3.create(riderPosition.alongM, local.y + liftM, riderPosition.acrossM),
    );
    return Vec3.add(this.riderBase(), offset);
  }
}

function moveToward(from: DeckPosition, to: DeckPosition, maxStepM: number): DeckPosition {
  const dAlong = to.alongM - from.alongM;
  const dAcross = to.acrossM - from.acrossM;
  const distance = Math.hypot(dAlong, dAcross);
  if (distance <= maxStepM || distance === 0) return to;
  const k = maxStepM / distance;
  return DeckPosition.create(from.alongM + dAlong * k, from.acrossM + dAcross * k);
}
